import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "diamonds-number-range px-4 pt-4 pb-2 min-w-[300px]" }
const _hoisted_2 = { class: "flex justify-between gap-8 mt-8" }
const _hoisted_3 = { class: "flex-1" }
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "flex-1 text-right" }
const _hoisted_6 = ["value"]

import { computed, ref, onMounted, onUnmounted, watch } from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'diamondsSearchFormNumberRange',
  props: {
    filter: { default: () => ({}) },
    activeValue: { default: () => ({ min: 0, max: 0 }) }
  },
  emits: ["update"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const min = computed(() => props.filter.min ?? 0);
const max = computed(() => props.filter.max ?? 100);
const step = computed(() => props.filter.step ?? 0.01);

const isPrice = computed(() => props.filter.unit === '€');
const isSize = computed(() => props.filter.unit === 'ct.');

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref<'min' | 'max' | null>(null);

const localMin = ref(props.activeValue?.min ?? min.value);
const localMax = ref(props.activeValue?.max ?? max.value);

watch(() => props.activeValue, (newVal) => {
  if (!isDragging.value) {
    localMin.value = newVal?.min ?? min.value;
    localMax.value = newVal?.max ?? max.value;
  }
}, { deep: true });

const currentMin = computed({
  get: () => localMin.value,
  set: (val) => {
    localMin.value = Math.min(val, localMax.value);
    emit("update", { min: localMin.value, max: localMax.value });
  }
});

const currentMax = computed({
  get: () => localMax.value,
  set: (val) => {
    localMax.value = Math.max(val, localMin.value);
    emit("update", { min: localMin.value, max: localMax.value });
  }
});

const getPercentage = (value: number) => {
  return ((value - min.value) / (max.value - min.value)) * 100;
};

const minPercent = computed(() => getPercentage(currentMin.value));
const maxPercent = computed(() => getPercentage(currentMax.value));

const handleMouseDown = (type: 'min' | 'max') => {
  isDragging.value = type;
};

const handleMouseMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value || !sliderRef.value) return;

  const rect = sliderRef.value.getBoundingClientRect();
  const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  const percentage = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
  let value = (percentage / 100) * (max.value - min.value) + min.value;
  
  // Snap to step
  if (isPrice.value || isSize.value) {
    value = selectOptions.value.reduce((prev, curr) => {
      return (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
    });
  } else {
    value = Math.round(value / step.value) * step.value;
  }
  
  // Ensure precision to avoid floating point issues
  value = Number(value.toFixed(2));

  if (isDragging.value === 'min') {
    localMin.value = Math.min(value, localMax.value);
  } else {
    localMax.value = Math.max(value, localMin.value);
  }
};

const handleMouseUp = () => {
  if (isDragging.value) {
    emit("update", { min: localMin.value, max: localMax.value });
  }
  isDragging.value = null;
};

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('touchmove', handleMouseMove, { passive: false });
  window.addEventListener('touchend', handleMouseUp);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('touchmove', handleMouseMove);
  window.removeEventListener('touchend', handleMouseUp);
});

const selectOptions = computed(() => {
  const options = [];
  
  if (isPrice.value) {
    let cur = min.value;
    while (cur <= max.value) {
      options.push(Number(cur.toFixed(2)));
      if (cur < 2000) cur += 100;
      else if (cur < 10000) cur += 500;
      else if (cur < 1000000) cur += 10000;
      else cur += 250000;
    }
  } else if (isSize.value) {
    let curSize = min.value;
    while (curSize <= max.value) {
      options.push(Number(curSize.toFixed(2)));
      if (curSize < 1) {
        curSize += 0.1;
      } else if (curSize < 2) {
        curSize += 0.25;
      } else if (curSize < 3) {
        curSize += 0.25;
      } else if (curSize < 4) {
        curSize += 0.5;
      } else if (curSize < 10) {
        curSize += 1;
      } else if (curSize <= 20) {
        curSize += 5;
      } else {
        curSize += 10;
      }

      curSize = Math.round(curSize * 100) / 100;
    }
  } else {
    const range = max.value - min.value;
    let optStep = step.value;
    if (range > 1000) optStep = 1000;
    else if (range > 100) optStep = 10;
    else if (range > 10) optStep = 1;
    else if (range > 1) optStep = 0.1;

    for (let i = min.value; i <= max.value; i += optStep) {
      options.push(Number(i.toFixed(2)));
    }
  }

  if (options[options.length - 1] !== max.value) {
    options.push(max.value);
  }
  return options;
});

const formatValue = (val: number) => {
  const formatted = val === max.value
    ? '∞'
    : val.toLocaleString('de-DE', {
        minimumFractionDigits: val % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      });
  return formatted + (props.filter.unit ? ' ' + props.filter.unit : '');
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "relative w-full h-1 bg-gray-200 rounded-full mb-8",
      ref_key: "sliderRef",
      ref: sliderRef
    }, [
      _createElementVNode("div", {
        class: "absolute h-full bg-brand-main rounded-full",
        style: _normalizeStyle({ left: minPercent.value + '%', right: (100 - maxPercent.value) + '%' })
      }, null, 4),
      _createElementVNode("div", {
        class: "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm",
        style: _normalizeStyle({ left: minPercent.value + '%' }),
        onMousedown: _cache[0] || (_cache[0] = ($event: any) => (handleMouseDown('min'))),
        onTouchstart: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (handleMouseDown('min')), ["prevent"]))
      }, null, 36),
      _createElementVNode("div", {
        class: "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm",
        style: _normalizeStyle({ left: maxPercent.value + '%' }),
        onMousedown: _cache[2] || (_cache[2] = ($event: any) => (handleMouseDown('max'))),
        onTouchstart: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (handleMouseDown('max')), ["prevent"]))
      }, null, 36)
    ], 512),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[6] || (_cache[6] = _createElementVNode("label", { class: "block text-[10px] text-gray-400 uppercase tracking-wider mb-1" }, "Von", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((currentMin).value = $event)),
          class: "w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectOptions.value.filter(o => o <= currentMax.value), (opt) => {
            return (_openBlock(), _createElementBlock("option", {
              key: 'min-'+opt,
              value: opt
            }, _toDisplayString(formatValue(opt)), 9, _hoisted_4))
          }), 128))
        ], 512), [
          [
            _vModelSelect,
            currentMin.value,
            void 0,
            { number: true }
          ]
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-[10px] text-gray-400 uppercase tracking-wider mb-1" }, "Bis", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((currentMax).value = $event)),
          class: "w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectOptions.value.filter(o => o >= currentMin.value), (opt) => {
            return (_openBlock(), _createElementBlock("option", {
              key: 'max-'+opt,
              value: opt
            }, _toDisplayString(formatValue(opt)), 9, _hoisted_6))
          }), 128))
        ], 512), [
          [
            _vModelSelect,
            currentMax.value,
            void 0,
            { number: true }
          ]
        ])
      ])
    ])
  ]))
}
}

})